import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type { PreferredTiming } from '@/types';
import SlotToggleRow from './SlotToggleRow';
import PreferredTimePicker from './PreferredTimePicker';

interface PreferredTimingToggleRowProps {
  // null = Anytime (toggle off); 'early' | 'mid' | 'late' = a chosen bucket.
  value: PreferredTiming | null;
  onChange: (value: PreferredTiming | null) => void;
  // Forwarded to SlotToggleRow — false inside SlotModal's merged automation card.
  bordered?: boolean;
  // Forwarded to SlotToggleRow — compact one-line layout inside the automation strip.
  compact?: boolean;
}

// Preferred-timing opt-in (spec v1.1) — a peer of the auto-select / recurring
// automation toggles, built on the SAME shared SlotToggleRow so its switch,
// spacing and border are pixel-identical to the "Automation (optional)" rows.
// Turning it ON pre-selects Mid (the middle bucket) and reveals the Early / Mid
// / Late picker + a persistent best-effort disclaimer as the row's `extra`;
// turning it OFF clears back to Anytime. No new visual vocabulary — the picker
// reuses the slot-button styling and the disclaimer reuses the amber-50/700
// AlertTriangle construction already used by the auto-select no-slot hint.
const PreferredTimingToggleRow = ({
  value,
  onChange,
  bordered = true,
  compact = false,
}: PreferredTimingToggleRowProps) => {
  const { t } = useTranslation();

  const extra = (
    <div className='mt-3'>
      <PreferredTimePicker value={value ?? 'mid'} onChange={(timing) => onChange(timing)} />
      <div className='mt-2.5 flex items-start gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-medium leading-5 text-amber-700'>
        <span className='flex h-5 shrink-0 items-center'>
          <AlertTriangle size={12} />
        </span>
        <span>{String(t('preferred_timing_disclaimer'))}</span>
      </div>
    </div>
  );

  return (
    <SlotToggleRow
      label={String(t('preferred_timing_toggle_label'))}
      description={String(
        t(compact ? 'preferred_timing_toggle_helper_short' : 'preferred_timing_toggle_description'),
      )}
      checked={value != null}
      // Opt in → default to Mid (the middle bucket); opt out → Anytime (null).
      onChange={(checked) => onChange(checked ? 'mid' : null)}
      extra={extra}
      bordered={bordered}
      compact={compact}
    />
  );
};

export default PreferredTimingToggleRow;
